import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Mail, MessageCircle } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { whatsappLink, MAILTO, EMAIL, WHATSAPP_DISPLAY } from "@/lib/api";
import { NAV } from "@/content/site";
import { Reveal } from "./Reveal";
import { useGoTo } from "./Header";

export function Contact() {
  const { openDemo, setBookingOpen, chosenExample } = useUI();
  return (
    <section id="kontakt" className="px-3 py-6 sm:px-5" data-testid="contact-section">
      <div className="relative overflow-hidden rounded-[28px] bg-ink py-20 text-paper sm:rounded-[36px] sm:py-28">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -right-20 -top-20 h-[520px] w-[520px] rounded-full border border-gold/20" />
        <div className="wrap relative">
          <Reveal>
            <p className="eyebrow text-gold">07 — Kontakt</p>
            <h2 className="mt-6 max-w-[900px] font-display text-[38px] font-semibold leading-[1.02] tracking-[-0.035em] text-white sm:text-6xl lg:text-[72px]">
              Entdecken Sie, was aus Ihrer Website <span className="font-serif font-normal italic text-gold">werden kann.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <button type="button" data-testid="contact-demo-cta" onClick={() => openDemo()}
              className="group flex min-h-[180px] flex-col justify-between rounded-2xl bg-gold p-7 text-left text-ink transition-transform active:scale-[0.99]">
              <span className="eyebrow text-ink/70">Kostenlos · innerhalb von 24 Stunden</span>
              <span className="flex items-end justify-between gap-4">
                <span className="font-display text-[28px] font-semibold leading-tight tracking-tight sm:text-[36px]">Kostenlose Demo anfordern</span>
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-ink text-paper transition-transform group-hover:translate-x-1"><ArrowRight className="h-5 w-5" /></span>
              </span>
            </button>
            <button type="button" data-testid="contact-booking-cta" onClick={() => setBookingOpen(true)}
              className="group flex min-h-[180px] flex-col justify-between rounded-2xl border border-white/15 p-7 text-left transition-colors hover:border-gold">
              <CalendarClock className="h-6 w-6 text-gold" />
              <span>
                <span className="block font-display text-[24px] font-semibold tracking-tight text-white">15-Minuten-Gespräch auswählen</span>
                <span className="mt-1 block text-[14px] text-zinc-300">Telefonisch · täglich rund um die Uhr · Zeitzone Europe/Vienna</span>
              </span>
            </button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <a href={whatsappLink(chosenExample)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
              className="flex items-center gap-4 rounded-2xl border border-white/15 p-5 transition-colors hover:border-gold">
              <MessageCircle className="h-5 w-5 text-gold" /><span><span className="block text-[13px] text-zinc-400">WhatsApp</span><span className="font-semibold text-white">{WHATSAPP_DISPLAY}</span></span>
            </a>
            <a href={MAILTO} data-testid="contact-email-link" className="flex items-center gap-4 rounded-2xl border border-white/15 p-5 transition-colors hover:border-gold">
              <Mail className="h-5 w-5 text-gold" /><span className="min-w-0"><span className="block text-[13px] text-zinc-400">E-Mail</span><span className="block truncate font-semibold text-white">{EMAIL}</span></span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const goTo = useGoTo();
  return (
    <footer className="pb-10 pt-16" data-testid="site-footer">
      <div className="wrap">
        <div className="grid gap-10 border-b border-line pb-12 md:grid-cols-[1.2fr_1fr_1fr]">
          <div>
            <img src="/logo-stack.png" alt="Webhaus – Wo professionelle und moderne Websites ihren Ursprung haben." loading="lazy" className="w-[220px] mix-blend-multiply" />
            <p className="mt-4 text-[14px] text-mute">Wien, Österreich</p>
          </div>
          <nav aria-label="Footer-Navigation" className="flex flex-col gap-2 text-[15px]">
            <span className="eyebrow mb-2 text-mute">Navigation</span>
            {NAV.map((n) => <a key={n.id} href={`/#${n.id}`} onClick={(e) => { e.preventDefault(); goTo(n.id); }} data-testid={`footer-nav-${n.id}`} className="w-fit hover:text-gold-deep">{n.label}</a>)}
          </nav>
          <div className="flex flex-col gap-2 text-[15px]">
            <span className="eyebrow mb-2 text-mute">Kontakt</span>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="w-fit hover:text-gold-deep">WhatsApp {WHATSAPP_DISPLAY}</a>
            <a href={MAILTO} data-testid="footer-email-link" className="w-fit break-all hover:text-gold-deep">{EMAIL}</a>
          </div>
        </div>
        <div className="flex flex-col justify-between gap-4 pt-6 text-[13px] text-mute sm:flex-row">
          <span>© {new Date().getFullYear()} Webhaus · Wien, Österreich</span>
          <span className="flex gap-6">
            <Link to="/impressum" data-testid="footer-impressum-link" className="hover:text-ink">Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz-link" className="hover:text-ink">Datenschutz</Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
